import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, PauseCircle, Wrench } from 'lucide-react';
import { api } from '../api';
import { franjaDeServicios } from './franjaServicios.js';

// DEGRADACIÓN DIGNA, NUNCA PANTALLA MUERTA · F1·A3.
//
// Cuando un servicio secundario (asistente, correo, nube, bot) está en
// pausa, o cuando la base no responde, esta franja lo dice en UNA línea
// humana en vez de dejar a la persona ante un error. El texto lo arma el
// servidor (`/api/estado-servicios`), en neutro y sin culpa; aquí solo se
// pinta con el tono que corresponde.
//
// SI LA PETICIÓN FALLA NO SE PINTA NADA, igual que el aviso de licencia: una
// franja que apareciera cada vez que el nodo tiene un mal segundo enseñaría
// a ignorarla, que es justo lo que no debe pasar el día que diga algo.

export default function AvisoServicios() {
  const [estado, setEstado] = useState(null);
  useEffect(() => {
    let vivo = true;
    const mirar = () => api.get('/estado-servicios')
      .then((d) => { if (vivo) setEstado(d); })
      .catch(() => {});
    mirar();
    // Cada minuto: un servicio en pausa se resuelve en minutos, no en horas,
    // y la persona tiene que enterarse de que ya volvió.
    const t = setInterval(mirar, 60 * 1000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  if (!estado) return null;

  // La base caída manda: modo lectura, en rojo, sin poder cerrarse.
  if (estado.base === 'solo_lectura') {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 px-4 py-2 text-xs bg-red-700 text-white"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{estado.aviso}</span>
      </div>
    );
  }

  // A6 · Una interrupción de un servicio que afecta al cliente: rojo, la app
  // sigue usable (no es modo lectura), pero se avisa. El texto lo arma el
  // servidor desde el vigía de disponibilidad, en neutro y sin culpa.
  if (estado.incidencia && estado.incidencia.mensaje) {
    return (
      <div
        role="status"
        className="flex items-start gap-2 border-b border-red-300 bg-red-100
                   px-4 py-2 text-xs text-red-900"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{estado.incidencia.mensaje}</span>
      </div>
    );
  }

  // F7 · La ventana de mantenimiento: AMARILLA, distinta del rojo de una
  // interrupción. Aparece 24 h antes, insiste en la última hora y dice «en
  // mantenimiento ahora» durante la ventana; el servidor decide cuál y la
  // quita al pasar la ventana.
  const franja = franjaDeServicios(estado);
  if (franja && franja.tono === 'mantenimiento') {
    return (
      <div
        role="status"
        data-franja="mantenimiento"
        data-fase={franja.fase}
        className="flex items-start gap-2 border-b border-yellow-300 bg-yellow-100
                   px-4 py-2 text-xs text-yellow-900"
      >
        <Wrench className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{franja.mensaje}</span>
      </div>
    );
  }

  // P11 · La hora del servidor desfasada: AMARILLA (atención). La arma el
  // servidor con lo que midió y dice qué revisar; solo le llega a quien
  // administra.
  if (franja && franja.tono === 'reloj') {
    return (
      <div
        role="status"
        data-franja="reloj"
        className="flex items-start gap-2 border-b border-yellow-300 bg-yellow-100
                   px-4 py-2 text-xs text-yellow-900"
      >
        <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{franja.mensaje}</span>
      </div>
    );
  }

  // Un servicio secundario en pausa: ámbar, la app sigue.
  if (estado.aviso) {
    return (
      <div
        role="status"
        className="flex items-start gap-2 border-b border-amber-300 bg-amber-100
                   px-4 py-2 text-xs text-amber-900"
      >
        <PauseCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{estado.aviso}</span>
      </div>
    );
  }

  return null;
}
